// Web Audio engine: buses, positional voices, samples with a procedural fallback. Every public call is a no-op
// when audio is unavailable or blocked, so the game never depends on it.
import type { Vec3 } from '../sim/math';
import type { Surface } from '../sim/terrain';
import { AudioManifest, parseManifest, pickFormat, shortLoops } from './manifest';
import { mixLayers, ROLL_FLAVOUR, RollFlavour } from './model';
import { EngineVoice, ProceduralEngine, loopNoise, noiseBuffer, playChirp, playNoiseBurst, playThump } from './procedural';

export type LevelKind = 'ring' | 'track' | 'enduro';

export interface BikeSound { rpm: number; load: number; skid: number; roll: number; surface: Surface }

const ROLL_FILTER: Record<RollFlavour, [BiquadFilterType, number]> = {
  dirt: ['lowpass', 450], sand: ['bandpass', 1100], grass: ['highpass', 1800], mud: ['lowpass', 250], water: ['bandpass', 2400],
};

class SampleEngine implements EngineVoice {
  readonly output: GainNode;
  private layers: { src: AudioBufferSourceNode; gain: GainNode }[];
  private off: { src: AudioBufferSourceNode; gain: GainNode } | null;

  constructor(ctx: BaseAudioContext, private loopRpms: number[], loops: AudioBuffer[], off: AudioBuffer | null, private offRpm: number) {
    this.output = ctx.createGain();
    const mk = (b: AudioBuffer) => {
      const src = ctx.createBufferSource();
      src.buffer = b;
      src.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      src.connect(gain).connect(this.output);
      src.start(0, Math.random() * b.duration); // de-phase layers
      return { src, gain };
    };
    this.layers = loops.map(mk);
    this.off = off ? mk(off) : null;
  }

  update(rpm: number, load: number, gain: number, t: number): void {
    const m = mixLayers(rpm, load, this.loopRpms, this.offRpm);
    const k = 0.03;
    m.layers.forEach((l, i) => {
      // Without an off-throttle loop, fake it by dropping the on-throttle layers.
      this.layers[i]!.gain.gain.setTargetAtTime(l.gain * (this.off ? 1 : 1 - 0.5 * m.offGain), t, k);
      this.layers[i]!.src.playbackRate.setTargetAtTime(l.rate, t, k);
    });
    if (this.off) {
      this.off.gain.gain.setTargetAtTime(m.offGain, t, k);
      this.off.src.playbackRate.setTargetAtTime(m.offRate, t, k);
    }
    this.output.gain.setTargetAtTime(gain, t, k);
  }

  stop(): void {
    for (const l of [...this.layers, ...(this.off ? [this.off] : [])]) l.src.stop();
    this.output.disconnect();
  }
}

function makePanner(ctx: BaseAudioContext, ref = 3): PannerNode {
  const p = ctx.createPanner();
  p.panningModel = 'HRTF';
  p.distanceModel = 'inverse';
  p.refDistance = ref;
  p.rolloffFactor = 1;
  p.maxDistance = 2000;
  return p;
}

function setPannerPos(p: PannerNode, v: Vec3, t: number): void {
  if (p.positionX) {
    p.positionX.setTargetAtTime(v.x, t, 0.02);
    p.positionY.setTargetAtTime(v.y, t, 0.02);
    p.positionZ.setTargetAtTime(v.z, t, 0.02);
  } else p.setPosition(v.x, v.y, v.z);
}

/** A bike voice (engine + tyre scrub + rolling noise) at a position in the world. */
export class BikeVoice {
  readonly panner: PannerNode;
  private engine: EngineVoice;
  private skidGain: GainNode;
  private rollGain: GainNode;
  private rollFilter: BiquadFilterNode;
  private sources: AudioBufferSourceNode[] = [];
  private flavour: RollFlavour | null = null;

  constructor(private a: AudioEngine, dest: AudioNode, private level: number) {
    const ctx = a.ctx!;
    this.panner = makePanner(ctx);
    this.panner.connect(dest);
    this.engine = a.makeEngineVoice();
    this.engine.output.connect(this.panner);

    const skidBand = ctx.createBiquadFilter();
    skidBand.type = 'bandpass';
    skidBand.frequency.value = 1600;
    skidBand.Q.value = 1.2;
    this.skidGain = ctx.createGain();
    this.skidGain.gain.value = 0;
    const n1 = loopNoise(ctx, a.noise!);
    n1.connect(skidBand).connect(this.skidGain).connect(this.panner);

    this.rollFilter = ctx.createBiquadFilter();
    this.rollGain = ctx.createGain();
    this.rollGain.gain.value = 0;
    const n2 = loopNoise(ctx, a.noise!);
    n2.connect(this.rollFilter).connect(this.rollGain).connect(this.panner);
    this.sources.push(n1, n2);
  }

  swapEngine(v: EngineVoice): void {
    this.engine.stop();
    this.engine = v;
    v.output.connect(this.panner);
  }

  update(s: BikeSound, pos: Vec3): void {
    const t = this.a.ctx!.currentTime;
    setPannerPos(this.panner, pos, t);
    this.engine.update(s.rpm, s.load, this.level, t);
    this.skidGain.gain.setTargetAtTime(s.skid * 0.35 * this.level, t, 0.04);
    const fl = ROLL_FLAVOUR[s.surface];
    if (fl !== this.flavour) {
      this.flavour = fl;
      const [type, f] = ROLL_FILTER[fl];
      this.rollFilter.type = type;
      this.rollFilter.frequency.setTargetAtTime(f, t, 0.05);
    }
    this.rollGain.gain.setTargetAtTime(s.roll * 0.25 * this.level, t, 0.05);
  }

  dispose(): void {
    this.engine.stop();
    for (const s of this.sources) s.stop();
    this.panner.disconnect();
  }
}

interface DistantBike { voice: BikeVoice; phase: number; radius: number; speed: number }

export class AudioEngine {
  ctx: AudioContext | null = null;
  noise: AudioBuffer | null = null;
  buses: { master: GainNode; engine: GainNode; sfx: GainNode; ambience: GainNode } | null = null;
  muted = false;
  private manifest: AudioManifest | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private voices: BikeVoice[] = [];
  private ambienceNodes: AudioNode[] = [];
  private distant: DistantBike[] = [];
  private level: LevelKind = 'ring';
  private birdTimer = 0;
  private clock = 0;

  /** Never throws; returns an engine that does nothing if Web Audio is missing. */
  static create(): AudioEngine {
    const e = new AudioEngine();
    try {
      const g = globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
      const Ctor = g.AudioContext ?? g.webkitAudioContext;
      if (!Ctor) return e;
      const ctx = new Ctor({ latencyHint: 'interactive' });
      e.ctx = ctx;
      e.noise = noiseBuffer(ctx, 2, 12345);
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -14;
      comp.knee.value = 8;
      comp.ratio.value = 4;
      comp.attack.value = 0.005;
      comp.release.value = 0.2;
      const master = ctx.createGain();
      master.gain.value = 0.8;
      master.connect(comp).connect(ctx.destination);
      const bus = (v: number) => { const n = ctx.createGain(); n.gain.value = v; n.connect(master); return n; };
      e.buses = { master, engine: bus(0.9), sfx: bus(1), ambience: bus(0.5) };
    } catch (err) {
      console.warn('audio unavailable', err);
      e.ctx = null;
      e.buses = null;
    }
    return e;
  }

  get running(): boolean { return this.ctx?.state === 'running'; }

  /** Resume on the first user gesture (autoplay policy). */
  unlockOn(target: EventTarget): void {
    if (!this.ctx) return;
    const events = ['pointerdown', 'keydown', 'touchstart'];
    const go = () => {
      this.ctx!.resume().then(() => events.forEach((ev) => target.removeEventListener(ev, go)), () => { /* still blocked */ });
    };
    events.forEach((ev) => target.addEventListener(ev, go));
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.ctx && this.buses) this.buses.master.gain.setTargetAtTime(m ? 0 : 0.8, this.ctx.currentTime, 0.05);
  }

  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  private hasSampleEngine(): boolean {
    const m = this.manifest;
    return !!m && m.engineLoops.length >= 2 && m.engineLoops.every((l) => this.buffers.has(l.name));
  }

  get usingSamples(): boolean { return this.hasSampleEngine(); }

  /** Loads manifest + samples. Resolves false (keeping the procedural sounds) on any failure. */
  /** `short`: only idle, a middle and the top engine loop (shortLoops); buffers no longer listed are dropped. */
  async loadSamples(base = 'audio/', short = false): Promise<boolean> {
    if (!this.ctx) return false;
    try {
      const res = await fetch(base + 'manifest.json');
      if (!res.ok) return false;
      const m = parseManifest(await res.json());
      if (!m) return false;
      if (short) m.engineLoops = shortLoops(m.engineLoops);
      const probe = typeof document !== 'undefined' ? document.createElement('audio') : null;
      const fmt = pickFormat(m.formats, (t) => probe?.canPlayType(t) ?? '');
      if (!fmt) return false;
      const names = new Set<string>([
        ...m.engineLoops.map((l) => l.name),
        ...(m.offLoop ? [m.offLoop.name] : []),
        ...Object.values(m.oneShots).flat(),
        ...Object.values(m.ambience).flat(),
      ].filter((n): n is string => typeof n === 'string'));
      await Promise.all([...names].map(async (n) => {
        try {
          const r = await fetch(`${base}${n}.${fmt}`);
          if (r.ok) this.buffers.set(n, await this.ctx!.decodeAudioData(await r.arrayBuffer()));
        } catch (err) { console.warn('audio sample failed', n, err); }
      }));
      for (const k of [...this.buffers.keys()]) if (!names.has(k)) this.buffers.delete(k);
      this.manifest = m;
      if (this.hasSampleEngine()) for (const v of this.voices) v.swapEngine(this.makeEngineVoice());
      this.setLevel(this.level); // rebuild ambience with the beds
      return true;
    } catch (err) {
      console.warn('audio samples unavailable, using procedural sound', err);
      return false;
    }
  }

  makeEngineVoice(): EngineVoice {
    const ctx = this.ctx!;
    const m = this.manifest;
    if (m && this.hasSampleEngine()) {
      const off = m.offLoop ? this.buffers.get(m.offLoop.name) ?? null : null;
      return new SampleEngine(ctx, m.engineLoops.map((l) => l.rpm!), m.engineLoops.map((l) => this.buffers.get(l.name)!), off,
        m.offLoop?.rpm ?? 5000);
    }
    return new ProceduralEngine(ctx, this.noise!);
  }

  createBike(level = 1): BikeVoice | null {
    if (!this.ctx || !this.buses) return null;
    const v = new BikeVoice(this, this.buses.engine, level);
    this.voices.push(v);
    return v;
  }

  setListener(pos: Vec3, forward: Vec3): void {
    if (!this.ctx) return;
    const l = this.ctx.listener, t = this.ctx.currentTime;
    if (l.positionX) {
      l.positionX.setTargetAtTime(pos.x, t, 0.02);
      l.positionY.setTargetAtTime(pos.y, t, 0.02);
      l.positionZ.setTargetAtTime(pos.z, t, 0.02);
      l.forwardX.setTargetAtTime(forward.x, t, 0.02);
      l.forwardY.setTargetAtTime(forward.y, t, 0.02);
      l.forwardZ.setTargetAtTime(forward.z, t, 0.02);
      l.upX.value = 0; l.upY.value = 1; l.upZ.value = 0;
    } else {
      l.setPosition(pos.x, pos.y, pos.z);
      l.setOrientation(forward.x, forward.y, forward.z, 0, 1, 0);
    }
  }

  private sfxAt(pos: Vec3): PannerNode {
    const p = makePanner(this.ctx!, 4);
    setPannerPos(p, pos, this.ctx!.currentTime);
    p.connect(this.buses!.sfx);
    setTimeout(() => p.disconnect(), 2500);
    return p;
  }

  private oneShot(kind: 'landing' | 'splash', strength: number, pos: Vec3): boolean {
    const names = this.manifest?.oneShots[kind];
    if (!names?.length) return false;
    const buf = this.buffers.get(names[Math.floor(Math.random() * names.length)]!);
    if (!buf) return false;
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.gain.value = strength;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = 0.9 + Math.random() * 0.2;
    src.connect(g).connect(this.sfxAt(pos));
    src.start();
    return true;
  }

  landing(strength: number, pos: Vec3): void {
    if (!this.running || strength <= 0) return;
    if (!this.oneShot('landing', strength, pos)) playThump(this.ctx!, this.sfxAt(pos), this.noise!, strength, this.ctx!.currentTime);
  }

  splash(strength: number, pos: Vec3): void {
    if (!this.running || strength <= 0) return;
    if (!this.oneShot('splash', strength, pos)) {
      playNoiseBurst(this.ctx!, this.sfxAt(pos), this.noise!, 'bandpass', 1300, 0.6 * strength, 0.5, this.ctx!.currentTime);
    }
  }

  /** Ambience: track = distant bikes + wind, enduro = forest wind + birds, ring = light wind. */
  setLevel(kind: LevelKind): void {
    this.level = kind;
    if (!this.ctx || !this.buses) return;
    const ctx = this.ctx;
    for (const n of this.ambienceNodes) {
      if (n instanceof AudioScheduledSourceNode) n.stop();
      n.disconnect();
    }
    this.ambienceNodes = [];
    for (const d of this.distant) d.voice.dispose();
    this.voices = this.voices.filter((v) => !this.distant.some((d) => d.voice === v));
    this.distant = [];

    const beds = (this.manifest?.ambience[kind] ?? []).map((n) => this.buffers.get(n)).filter((b): b is AudioBuffer => !!b);
    for (const b of beds) {
      const src = ctx.createBufferSource();
      src.buffer = b;
      src.loop = true;
      const g = ctx.createGain();
      g.gain.value = 0.7;
      src.connect(g).connect(this.buses.ambience);
      src.start(0, Math.random() * b.duration);
      this.ambienceNodes.push(src, g);
    }

    // Procedural wind under any bed: lowpassed noise with a slow gust LFO.
    const wind = loopNoise(ctx, this.noise!);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = kind === 'enduro' ? 500 : 320;
    const wg = ctx.createGain();
    wg.gain.value = beds.length ? 0.08 : 0.18;
    const gust = ctx.createOscillator();
    gust.frequency.value = 0.07;
    const gustDepth = ctx.createGain();
    gustDepth.gain.value = wg.gain.value * 0.6;
    gust.connect(gustDepth).connect(wg.gain);
    gust.start();
    wind.connect(lp).connect(wg).connect(this.buses.ambience);
    this.ambienceNodes.push(wind, lp, wg, gust, gustDepth);

    // Distant bikes at the track: procedural voices circling far away, unless a multi-bike bed was loaded.
    if (kind === 'track' && beds.length === 0) {
      for (let i = 0; i < 3; i++) {
        const voice = new BikeVoice(this, this.buses.ambience, 0.6);
        this.voices.push(voice);
        this.distant.push({ voice, phase: i * 2.1, radius: 90 + i * 35, speed: 0.08 + i * 0.02 });
      }
    }
  }

  /** Per-frame upkeep for ambience: distant bikes circle the listener, birds chirp in the forest. */
  tick(dt: number, around: Vec3): void {
    if (!this.running) return;
    this.clock += dt;
    const T = this.clock;
    for (const d of this.distant) {
      const a = d.phase + T * d.speed;
      const wave = Math.sin(T * 0.9 + d.phase * 3);
      d.voice.update(
        { rpm: 5500 + 2500 * wave + 800 * Math.sin(T * 2.3 + d.phase), load: 0.6 + 0.4 * wave, skid: 0, roll: 0, surface: 'clay' },
        { x: around.x + Math.cos(a) * d.radius, y: around.y + 3, z: around.z + Math.sin(a) * d.radius },
      );
    }
    if (this.level === 'enduro') { // the forest bed is wind only, so birds stay procedural
      this.birdTimer -= dt;
      if (this.birdTimer <= 0) {
        const t = this.ctx!.currentTime;
        const pitch = 2500 + Math.random() * 2500;
        const n = 1 + Math.floor(Math.random() * 4);
        for (let i = 0; i < n; i++) playChirp(this.ctx!, this.buses!.ambience, t + i * 0.16, pitch * (1 + 0.05 * i));
        this.birdTimer = 1.5 + Math.random() * 5;
      }
    }
  }
}
